import { UPDATE_CHAT_NAME, USER_SET_CID, USER_SET_REF, USER_SET_TTCLID } from '@/assets/config/api.config'
import axios from 'axios'
import { getAuth } from 'firebase/auth'
import { createStore } from 'vuex'

export default createStore({
  state: {
    styles: [],
    models: [],
    currentChat: {
      title: 'New Chat'
    },
    user: null,
    chatList: null,
    userDoc: null,
    userDocListenerEnabled: false,
    mainFolders: [],
    allFolders: [],
    smallMenuLayout: false,
    showCreateFolder: false,
    showSearchPopup: false,
    showEditFolder: false,
    folderToBeEdited: null,
    showMoveFolderOrChat: false,
    folderToMove: null,
    chatToMove: null,
    showCannotMoveFolder: false,
    showCannotCreateFolder: false,
    showOfferPopup: false,
    showPaidTrialPopup: false,
    selectedModel: null,
    selectedStyle: null,
    showSelectStyle: false,
    promptImproverLimitReached: false,
    emptyChatSystemPrompt: '',
    isShortcutMenuOpened: false,
    canvaActive: false,
    canvaDBDocument: null,
    showModelPopup: false,
    importedTextToCanva: '',
    isCanvaElaborating: false,
    isCanvaOptionSelected: false,
    showPremiumPopup: false,
    showNewChatForbidden: false,
    showModelWarningPopup: false,
    modelWarningContent: '',
    currentChatTotalTokens: 0,
    showModelSpeedInfoPopup: false,
    showSpeedRestorePopup: false,
    showThunderAttachmentWarning: false,
    isWebSearchActive: false,
    // DeepResearch state
    isDeepResearchActive: false,
    deepResearchLimitReached: false,
    isDeepResearchPending: false,
    pendingDeepResearchQuery: '',
  },
  getters: {
    getStyles: state => state.styles,
    getModels: state => state.models,
    getUser: state => state.user,
    getCurrentChat: state => state.currentChat,
    getChatList: state => state.chatList,
    getUserDoc: state => state.userDoc,
    getUserDocListenerEnabled: state => state.userDocListenerEnabled,
    getSmallMenuLayout: state => state.smallMenuLayout,
    getMainFolders: state => state.mainFolders,
    getAllFolders: state => state.allFolders,
    showCreateFolder: state => state.showCreateFolder,
    showSearchPopup: state => state.showSearchPopup,
    showEditFolder: state => state.showEditFolder,
    folderToBeEdited: state => state.folderToBeEdited,
    showMoveFolderOrChat: state => state.showMoveFolderOrChat,
    folderToMove: state => state.folderToMove,
    chatToMove: state => state.chatToMove,
    showCannotMoveFolder: state => state.showCannotMoveFolder,
    showCannotCreateFolder: state => state.showCannotCreateFolder,
    showOfferPopup: state => state.showOfferPopup,
    showPaidTrialPopup: state => state.showPaidTrialPopup,
    getSelectedModel: state => state.selectedModel,
    getSelectedStyle: state => state.selectedStyle,
    showSelectStyle: state => state.showSelectStyle,
    getPromptImproverLimitReached: state => state.promptImproverLimitReached,
    getEmptyChatSystemPrompt: state => state.emptyChatSystemPrompt,
    isShortcutMenuOpened: state => state.isShortcutMenuOpened,
    isCanvaActive: state => state.canvaActive,
    getCanvaDBDocument: state => state.canvaDBDocument,
    getShowModelPopup: state => state.showModelPopup,
    getImportedTextToCanva: state => state.importedTextToCanva,
    isCanvaElaborating: state => state.isCanvaElaborating,
    isCanvaOptionSelected: state => state.isCanvaOptionSelected,
    getShowPremiumPopup: state => state.showPremiumPopup,
    getShowNewChatForbidden: state => state.showNewChatForbidden,
    getShowModelWarningPopup: state => state.showModelWarningPopup,
    getModelWarningContent: state => state.modelWarningContent,
    getCurrentChatTotalTokens: state => state.currentChatTotalTokens,
    getShowModelSpeedInfoPopup: state => state.showModelSpeedInfoPopup,
    getShowSpeedRestorePopup: state => state.showSpeedRestorePopup,
    getShowThunderAttachmentWarning: state => state.showThunderAttachmentWarning,
    getWebSearchActive: state => state.isWebSearchActive,
    // DeepResearch getters
    getDeepResearchActive: state => state.isDeepResearchActive,
    getDeepResearchLimitReached: state => state.deepResearchLimitReached,
    getIsDeepResearchPending: state => state.isDeepResearchPending,
    getPendingDeepResearchQuery: state => state.pendingDeepResearchQuery,
    getIsDeepResearchRunning: state => {
      // Use userDoc.activeDeepResearches instead of volatile state
      return state.userDoc && state.userDoc.activeDeepResearches > 0;
    }
  },
  mutations: {
    setStyles(state, styles) {
      state.styles = styles
    },
    setModels(state, models) {
      state.models = models 
    },
    setSelectedModel(state, model) {
      state.selectedModel = model
    },
    setSelectedStyle(state, style) {
      state.selectedStyle = style
    },
    setUser(state, user) {
      state.user = user
    },
    setSmallMenuLayout(state, layout) {
      state.smallMenuLayout = layout
    },
    setShowPremiumPopup(state, show) {
      state.showPremiumPopup = show
    },
    setShowModelSpeedInfoPopup(state, show) {
      state.showModelSpeedInfoPopup = show
    },
    setShowSpeedRestorePopup(state, show) {
      state.showSpeedRestorePopup = show
    },
    setCanvaActive(state, active) {
      state.canvaActive = active
    },
    setIsCanvaOptionSelected(state, selected) {
      state.isCanvaOptionSelected = selected
    },
    setCurrentChat(state, chat) {
      state.currentChat = chat
    },
    setChatList(state, chatList) {
      state.chatList = chatList
    },
    setShowCreateFolder(state, show) {
      state.showCreateFolder = show
    },
    setShowSearchPopup(state, show) {
      state.showSearchPopup = show
    },
    setShowEditFolder(state, show) {
      state.showEditFolder = show
    },
    setFolderToBeEdited(state, folder) {
      state.folderToBeEdited = folder
    },
    setShortcutMenuOpened(state, opened) {
      state.isShortcutMenuOpened = opened
    },
    setShowMoveFolderOrChat(state, show) {
      state.showMoveFolderOrChat = show
    },
    setFolderToMove(state, folder) {
      state.folderToMove = folder
    },
    setChatToMove(state, chat) {
      state.chatToMove = chat
    },
    setShowCannotMoveFolder(state, show) {
      state.showCannotMoveFolder = show
    },
    setShowCannotCreateFolder(state, show) {
      state.showCannotCreateFolder = show
    },
    setShowOfferPopup(state, show) {
      state.showOfferPopup = show
    },
    setShowPaidTrialPopup(state, show) {
      state.showPaidTrialPopup = show
    },
    setPromptImproverLimitReached(state, show) {
      state.promptImproverLimitReached = show
    },
    setEmptyChatSystemPrompt(state, prompt) {
      state.emptyChatSystemPrompt = prompt
    },
    async updateChatName(state, payload) {
      state.chatList.forEach(chat => {
        if (chat.id === payload.chatID) {
          chat.title = payload.title
        }
      })

      if (state.currentChat.id === payload.chatID) {
        state.currentChat.title = payload.title
      }

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        return
      }
      const token = await user.getIdToken()
      await axios.patch(UPDATE_CHAT_NAME, {
        chatID: payload.chatID,
        title: payload.title
      }, {
        headers: {
          Authorization: token
        }
      })
    },
    async setUserDoc(state, userDoc) {
      state.userDoc = userDoc
      const c_id = localStorage.getItem('c_id');
      const ref = localStorage.getItem('ref');
      const ttclid = localStorage.getItem('ttclid');
      if(!userDoc.c_id && c_id){
        const auth = getAuth();
        const user = auth.currentUser;
        const token = await user.getIdToken()
        const gclid = localStorage.getItem('gclid');
        await axios.post(USER_SET_CID, {
          campaignID: c_id,
          gclid: gclid
        }, {
          headers: {
            Authorization: token
          }
        });
      }

      if(!userDoc.ttclid && ttclid){
        const auth = getAuth();
        const user = auth.currentUser;
        const token = await user.getIdToken()
        await axios.post(USER_SET_TTCLID, {
          ttclid: ttclid
        }, {
          headers: {
            Authorization: token
          }
        });
      }

      if(!userDoc.ref && ref){
        const auth = getAuth();
        const user = auth.currentUser;
        const token = await user.getIdToken()
        await axios.post(USER_SET_REF, {
          ref: ref
        }, {
          headers: {
            Authorization: token
          }
        });
      }
    },
    setUserDocListenerEnabled(state, enabled) {
      state.userDocListenerEnabled = enabled
    },
    setMainFolders(state, folders) {
      state.mainFolders = folders
    },
    setAllFolders(state, folders) {
      state.allFolders = folders
    },
    setShowSelectStyle(state, show) {
      state.showSelectStyle = show
    },
    setShowModelPopup(state, show) {
      state.showModelPopup = show
    },
    setCanvaDBDocument(state, document) {
      state.canvaDBDocument = document
    },
    setImportedTextToCanva(state, text) {
      state.importedTextToCanva = text
    },
    setIsCanvaElaborating(state, elaborating) {
      state.isCanvaElaborating = elaborating
    },
    setShowNewChatForbidden(state, show) {
      state.showNewChatForbidden = show
    },
    setShowModelWarningPopup(state, show) {
      state.showModelWarningPopup = show
    },
    setModelWarningContent(state, content) {
      state.modelWarningContent = content
    },
    setCurrentChatTotalTokens(state, tokens) {
      state.currentChatTotalTokens = tokens
    },
    setShowThunderAttachmentWarning(state, show) {
      state.showThunderAttachmentWarning = show
    },
    setWebSearchActive(state, active) {
      state.isWebSearchActive = active
    },
    // DeepResearch mutations
    setDeepResearchActive(state, active) {
      state.isDeepResearchActive = active
    },
    setDeepResearchLimitReached(state, reached) {
      state.deepResearchLimitReached = reached
    },
    setIsDeepResearchPending(state, pending) {
      state.isDeepResearchPending = pending
    },
    setPendingDeepResearchQuery(state, query) {
      state.pendingDeepResearchQuery = query
    },
    setDefaultModel(state){
      const isPremium = state.userDoc && state.userDoc.isPremium;
      if (isPremium) {
        state.selectedModel = state.models.find(model => model.value === 'o3-mini');
      } else {
        state.selectedModel = state.models.find(model => model.value === 'gpt-3.5-turbo');
      }
    }
  },
  actions: {
  },
  modules: {
  }
})
